import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { Route, Routes } from "react-router-dom";
import {
  TradeMatchingFileType,
  TriggerTradeMatchingRunResult,
  jobKindForFileType,
  useTriggerTradeMatchingRun,
} from "./dataHooks/tradeMatchingQueries";
import { FileDetails } from "./fileDetails";
import { TradeMatchingFileList } from "./tradeMatchingFileList";
import { UploadDialog } from "./upload";

const TradeMatcherListView: FC = () => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [runResult, setRunResult] =
    useState<TriggerTradeMatchingRunResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [processingPath, setProcessingPath] = useState<string | null>(null);

  const triggerRun = useTriggerTradeMatchingRun({
    onSuccess: (result) => {
      setRunError(null);
      setRunResult(result);
      setProcessingPath(null);
    },
    onError: (error) => {
      setRunResult(null);
      setRunError(error.message);
      setProcessingPath(null);
    },
  });

  const handleProcessFile = (
    blobPath: string,
    fileType: TradeMatchingFileType,
  ) => {
    setProcessingPath(blobPath);
    triggerRun.mutate({
      blobPath,
      jobKind: jobKindForFileType(fileType),
    });
  };

  return (
    <Box
      sx={{
        padding: 3,
        maxWidth: 1800,
        margin: "0 auto",
      }}
    >
      <Box
        sx={{
          marginBottom: 4,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            color="text.primary"
            sx={{ fontWeight: 600, marginBottom: 1 }}
          >
            Trade Matcher
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 760 }}
          >
            Files uploaded for processing in the selected date range.
          </Typography>
        </Box>
        <Stack direction="row" sx={{ gap: 1.5 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload file
          </Button>
        </Stack>
      </Box>

      <Card elevation={7}>
        <CardContent sx={{ padding: 2 }}>
          <TradeMatchingFileList
            onProcessFile={handleProcessFile}
            processingPath={processingPath}
            isPending={triggerRun.isPending}
          />
        </CardContent>
      </Card>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

      <Snackbar
        open={runResult !== null}
        autoHideDuration={8000}
        onClose={() => setRunResult(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="success"
          onClose={() => setRunResult(null)}
          sx={{ width: "100%" }}
        >
          Run started:{" "}
          {runResult?.runUrl ? (
            <Link href={runResult.runUrl} target="_blank" rel="noreferrer">
              {runResult.runId}
            </Link>
          ) : (
            runResult?.runId
          )}
        </Alert>
      </Snackbar>

      <Snackbar
        open={runError !== null}
        autoHideDuration={8000}
        onClose={() => setRunError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          onClose={() => setRunError(null)}
          sx={{ width: "100%" }}
        >
          {runError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export const TradeMatcher: FC = () => {
  return (
    <Routes>
      <Route index element={<TradeMatcherListView />} />
      <Route path="file/:source" element={<FileDetails />} />
    </Routes>
  );
};
